import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Tab,
  Tabs
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Property, CreatePropertyDTO } from '../../types/property.types';
import { UtilityService } from '../../types/utility.types';
import { Quote } from '../../types/quote.types';
import { Document } from '../../types/document.types';
import { getProperty, createProperty, updateProperty } from '../../api/properties';
import { getUtilityServices } from '../../api/utilityServices';
import { getClient } from '../../api/clients';
import PropertyInfo from '../../components/Property/PropertyInfo';
import UtilityServices from '../../components/UtilityServices/UtilityServices';
import DocumentList from '../../components/Document/DocumentList';
import QuoteList from '../../components/Quote/QuoteList';
import PropertyForm from '../../components/Property/PropertyForm';
import NotesPanel from '../../components/Notes/NotesPanel';
import toast from 'react-hot-toast';
import { getDocuments, uploadDocument, deleteDocument } from '../../api/documents';
import * as quoteApi from '../../api/quotes';
import axios from '../../api/axiosConfig';

const PropertyDetails: React.FC = () => {
  const { id: clientId, propertyId } = useParams<{ id: string; propertyId: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | null>(null);
  const [client, setClient] = useState<any>(null);
  const [utilityServices, setUtilityServices] = useState<UtilityService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!clientId || !propertyId) {
        setError('Missing required parameters');
        setLoading(false);
        return;
      }

      // Don't fetch if we're creating a new property
      if (propertyId === 'new') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel
        const [
          propertyData,
          servicesData,
          documentsData,
          quotesData,
          clientData
        ] = await Promise.all([
          getProperty(clientId, propertyId),
          getUtilityServices(clientId, propertyId),
          getDocuments(clientId, propertyId),
          quoteApi.getPropertyQuotes(propertyId),
          getClient(clientId)
        ]);

        // Filter services to only include those belonging to this property
        const propertyServices = servicesData.filter(service => service.property?._id === propertyId) || [];

        // Update all state
        setProperty(propertyData);
        setClient(clientData);
        setUtilityServices(propertyServices);
        setDocuments(documentsData || []);
        setQuotes(quotesData || []);

        // Update property with latest utility services
        setProperty(prev => prev ? {
          ...prev,
          utilityServices: propertyServices
        } : null);
      } catch (err) {
        setError('Failed to load property details');
        toast.error('Failed to load property details');
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, [clientId, propertyId]);

  const fetchDocuments = useCallback(async () => {
    if (!clientId || !propertyId) return;
    
    try {
      setDocumentLoading(true);
      const documents = await getDocuments(clientId, propertyId);
      setDocuments(documents);
    } catch (error) {
      
      toast.error('Failed to fetch documents');
    } finally {
      setDocumentLoading(false);
    }
  }, [clientId, propertyId]);

  const fetchQuotes = useCallback(async () => {
    if (!propertyId) return;
    
    try {
      setQuoteLoading(true);
      const data = await quoteApi.getPropertyQuotes(propertyId);
      
      setQuotes(data);
    } catch (error) {
      
      toast.error('Failed to fetch quotes');
    } finally {
      setQuoteLoading(false);
    }
  }, [propertyId]);

  // Refresh tab data when switching tabs
  useEffect(() => {
    if (!clientId || !propertyId || propertyId === 'new') return;

    const refreshTabData = async () => {
      try {
        switch (activeTab) {
          case 2: // Documents
            await fetchDocuments();
            break;
          case 3: // Quotes
            await fetchQuotes();
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('Failed to refresh tab data', error);
        toast.error('Failed to refresh data');
      }
    };

    refreshTabData();
  }, [activeTab, clientId, propertyId, fetchDocuments, fetchQuotes]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleViewQuote = (quoteId: string) => {
    navigate(`/utilities/quotes/${quoteId}/view`);
  };

  const handleUpdateProperty = async (propertyData: CreatePropertyDTO) => {
    try {
      if (!clientId || !propertyId) return;
      const updatedProperty = await updateProperty(clientId, propertyId, propertyData);
      setProperty(updatedProperty);
      toast.success('Property updated successfully');
      setEditDialogOpen(false);
    } catch (error) {
      
      toast.error('Failed to update property');
    }
  };

  const handleDocumentUpload = async (file: File) => {
    if (!clientId || !propertyId) return;

    try {
      await uploadDocument(clientId, file, 'GENERAL', propertyId);
      toast.success('Document uploaded successfully');
      fetchDocuments(); // Refresh the documents list
    } catch (error) {
      
      toast.error('Failed to upload document');
    }
  };

  const handleViewDocument = async (document: Document) => {
    if (!document.url && !document.s3Key) return;
    
    try {
      // Generate a fresh pre-signed URL for viewing
      const response = await axios.get(`/clients/${clientId}/properties/${propertyId}/documents/${document._id}/download`);
      
      if (response.data.success && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        toast.error('Failed to generate document URL');
      }
    } catch (error) {
      console.error('Error generating fresh document URL:', error);
      toast.error('Failed to open document');
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!clientId || !propertyId) return;

    try {
      await deleteDocument(clientId, documentId);
      toast.success('Document deleted successfully');
      fetchDocuments(); // Refresh documents list
    } catch (error) {
      
      toast.error('Failed to delete document');
    }
  };

  const handleCreateProperty = async (propertyData: CreatePropertyDTO) => {
    try {
      if (!clientId) return;
      const newProperty = await createProperty(clientId, propertyData);
      toast.success('Property created successfully');
      navigate(`/utilities/clients/${clientId}/properties/${newProperty._id}`);
    } catch (error) {
      
      toast.error('Failed to create property');
    }
  };

  if (loading) {
    return (
      <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(12px)', borderRadius: 2, p: 2 }} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (propertyId === 'new') {
    if (!clientId) {
      return (
        <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(12px)', borderRadius: 2, p: 2 }}>
          <Alert severity="error">Missing client ID</Alert>
        </Box>
      );
    }

    // For new property, we need to force the dialog to be open
    // and handle the form submission properly
    return (
      <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(12px)', borderRadius: 2, p: 2 }}>
        <Breadcrumbs sx={{ mb: 2, '& .MuiTypography-root': { color: 'text.primary' } }}>
          <Link component={RouterLink} to="/utilities/clients" sx={{ color: 'text.primary' }}>
            Clients
          </Link>
          <Link component={RouterLink} to={`/utilities/clients/${clientId}`} sx={{ color: 'text.primary' }}>
            Client Details
          </Link>
          <Typography color="text.primary">New Property</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography variant="h6" color="text.primary">Creating new property...</Typography>
        </Box>
        
        <PropertyForm
          open={true}
          onClose={() => navigate(`/utilities/clients/${clientId}`)}
          onSubmit={handleCreateProperty}
          clientId={clientId}
          title="Add New Property"
        />
      </Box>
    );
  }

  if (error || !property) {
    return (
      <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(12px)', borderRadius: 2, p: 2 }}>
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ '& .MuiTypography-root': { color: 'text.primary' } }}>
                  <Link component={RouterLink} to="/utilities/clients" sx={{ color: 'text.primary' }}>
                    Clients
                  </Link>
                  <Link component={RouterLink} to={`/utilities/clients/${clientId}`} sx={{ color: 'text.primary' }}>
                    {client?.companyName || ''}
                  </Link>
                  <Typography color="text.primary">
                    {property?.name || (propertyId === 'new' ? 'New Property' : '')}
                  </Typography>
                </Breadcrumbs>
              </Box>
              <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.1)', color: 'text.primary' }}>
                {error || 'Property not found'}
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => navigate(`/utilities/clients/${clientId}`)}
                  sx={{
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  Back to Client
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(12px)', borderRadius: 2, p: 2 }}>
      <Breadcrumbs sx={{ mb: 2, '& .MuiTypography-root': { color: 'text.primary' } }}>
        <Link component={RouterLink} to="/utilities/clients" sx={{ color: 'text.primary' }}>
          Clients
        </Link>
        <Link component={RouterLink} to={`/utilities/clients/${clientId}`} sx={{ color: 'text.primary' }}>
          {client?.companyName || ''}
        </Link>
        <Typography color="text.primary">{property?.name}</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" color="text.primary">{property?.name}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => setEditDialogOpen(true)}
        >
          Edit Property
        </Button>
      </Box>
      
      <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        sx={{ 
          '& .MuiTab-root': { color: 'rgba(255, 255, 255, 0.7)' },
          '& .Mui-selected': { color: 'text.primary' },
          '& .MuiTabs-indicator': { backgroundColor: '#3b82f6' }
        }}
      >
        <Tab label="Property Info" sx={{ color: 'text.primary' }} />
        <Tab label="Utility Services" sx={{ color: 'text.primary' }} />
        <Tab label="Documents" sx={{ color: 'text.primary' }} />
        <Tab label="Quotes" sx={{ color: 'text.primary' }} />
        <Tab label="Notes" sx={{ color: 'text.primary' }} />
      </Tabs>
      
      {activeTab === 0 && (
        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" gutterBottom sx={{ 
            borderLeft: '4px solid #2e7d32', 
            pl: 2,
            py: 0.5,
            backgroundColor: 'rgba(46, 125, 50, 0.05)',
            borderRadius: '0 4px 4px 0',
            mb: 2
          }}>Property Information</Typography>
          <PropertyInfo property={property} />
        </Paper>
      )}
      
      {activeTab === 1 && (
        <Paper elevation={1} sx={{ p: 2, mt: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" gutterBottom sx={{ 
            borderLeft: '4px solid #2e7d32', 
            pl: 2,
            py: 0.5,
            backgroundColor: 'rgba(46, 125, 50, 0.05)',
            borderRadius: '0 4px 4px 0',
            mb: 2
          }}>Utility Contracts</Typography>
          <UtilityServices 
            propertyId={propertyId!} 
            clientId={clientId!} 
            services={utilityServices}
            onServicesChange={(newServices) => {
              setUtilityServices(newServices);
              setProperty(prev => prev ? {
                ...prev,
                utilityServices: newServices
              } : null);
            }}
          />
        </Paper>
      )}
      
      {activeTab === 2 && (
        <Paper elevation={1} sx={{ p: 2, mt: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" gutterBottom sx={{ 
            borderLeft: '4px solid #ed6c02', 
            pl: 2,
            py: 0.5,
            backgroundColor: 'rgba(237, 108, 2, 0.05)',
            borderRadius: '0 4px 4px 0',
            mb: 2
          }}>Property Documents</Typography>
          <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <input
                accept="*/*"
                style={{ display: 'none' }}
                id="document-upload"
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleDocumentUpload(file);
                    e.target.value = ''; // Reset the input
                  }
                }}
              />
              <label htmlFor="document-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<AddIcon />}
                  sx={{ 
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    }
                  }}
                >
                  Upload Document
                </Button>
              </label>
            </Box>
            <DocumentList
              clientId={clientId!}
              propertyId={propertyId!}
              documentType="property"
              documents={documents}
              onDocumentDeleted={fetchDocuments}
              onViewDocument={handleViewDocument}
              onDeleteDocument={handleDeleteDocument}
              loading={documentLoading}
            />
          </Box>
        </Paper>
      )}

      {activeTab === 3 && (
        <Paper elevation={1} sx={{ p: 2, mt: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" gutterBottom sx={{ 
            borderLeft: '4px solid #9c27b0', 
            pl: 2,
            py: 0.5,
            backgroundColor: 'rgba(156, 39, 176, 0.05)',
            borderRadius: '0 4px 4px 0',
            mb: 2
          }}>Quotes</Typography>
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box></Box> {/* Empty box for spacing */}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/utilities/quotes/new?clientId=${clientId}&propertyId=${propertyId}`)}
                sx={{ 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                  }
                }}
              >
                Add Quote
              </Button>
            </Box>
            <QuoteList 
              quotes={quotes}
              loading={quoteLoading}
              showClientName={false}
              showPropertyName={false}
              onViewQuote={handleViewQuote}
            />
          </Box>
        </Paper>
      )}

      {activeTab === 4 && (
        <Paper elevation={1} sx={{ p: 2, mt: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" gutterBottom sx={{ 
            borderLeft: '4px solid #f44336', 
            pl: 2,
            py: 0.5,
            backgroundColor: 'rgba(244, 67, 54, 0.05)',
            borderRadius: '0 4px 4px 0',
            mb: 2
          }}>Property Notes</Typography>
          <NotesPanel 
            entityType="property" 
            entityId={propertyId!} 
            title=""
          />
        </Paper>
      )}

      {/* Edit Property Dialog */}
      <PropertyForm
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={handleUpdateProperty}
        initialValues={property}
        title="Edit Property"
        clientId={clientId!}
      />
    </Box>
  );
};

export default PropertyDetails;
